/**
 * Mailing API — start/stop, settings, status, live monitor (SSE).
 *
 * The SSE stream uses native EventSource. Telegram WebApp initData has to be
 * passed as a query param (since EventSource doesn't support custom headers),
 * but our auth dep already accepts ``?init_data=...`` too. We fall back to
 * sending it in the URL.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { tg } from "@/lib/telegram";
import { api, ApiError } from "./client";

// ============================================================
// Types
// ============================================================

export type SendMode = "round_robin" | "single";
export type MailingMode = "classic" | "profiles";
export type SafetyPreset = "off" | "conservative" | "balanced" | "aggressive" | "custom";

export interface MailingSettings {
  is_mailing_enabled: boolean;
  active_mail_template_id: number | null;
  mail_interval_minutes: number;
  per_chat_delay_seconds: number;
  send_mode: SendMode;
  single_account_id: number | null;
  mailing_mode: MailingMode;
  hidden_mentions_enabled: boolean;
  quiet_mode_enabled: boolean;
  quiet_start_msk_minute: number;
  quiet_end_msk_minute: number;
  smart_pause_enabled: boolean;
  spintax_enabled: boolean;
  human_simulation_enabled: boolean;
  warmup_enabled: boolean;
  safety_preset: SafetyPreset;
  safety_delay_multiplier: number;
  safety_typing_min_cpm: number;
  safety_typing_max_cpm: number;
  safety_profile_view_chance_percent: number;
  safety_scroll_chance_percent: number;
  safety_typo_chance_percent: number;
  safety_delete_chance_percent: number;
  safety_sleep_enabled: boolean;
  safety_sleep_start_msk_minute: number;
  safety_sleep_end_msk_minute: number;
  safety_daily_action_limit_per_account: number;
  safety_flood_cooldown_multiplier: number;
  safety_action_lock_enabled: boolean;
  reactions_enabled: boolean;
  reaction_min_interval_minutes: number;
  reaction_max_interval_minutes: number;
  reaction_daily_limit_per_account: number;
  reaction_daily_limit_per_chat: number;
  reaction_author_cooldown_hours: number;
  reaction_skip_chance_percent: number;
  reaction_emojis: string[];
}

export interface MailingSettingsPatch {
  active_mail_template_id?: number | null;
  mail_interval_minutes?: number;
  per_chat_delay_seconds?: number;
  send_mode?: SendMode;
  single_account_id?: number | null;
  mailing_mode?: MailingMode;
  hidden_mentions_enabled?: boolean;
  quiet_mode_enabled?: boolean;
  quiet_start_msk_minute?: number;
  quiet_end_msk_minute?: number;
  smart_pause_enabled?: boolean;
  spintax_enabled?: boolean;
  human_simulation_enabled?: boolean;
  warmup_enabled?: boolean;
  safety_preset?: SafetyPreset;
  safety_delay_multiplier?: number;
  safety_typing_min_cpm?: number;
  safety_typing_max_cpm?: number;
  safety_profile_view_chance_percent?: number;
  safety_scroll_chance_percent?: number;
  safety_typo_chance_percent?: number;
  safety_delete_chance_percent?: number;
  safety_sleep_enabled?: boolean;
  safety_sleep_start_msk_minute?: number;
  safety_sleep_end_msk_minute?: number;
  safety_daily_action_limit_per_account?: number;
  safety_flood_cooldown_multiplier?: number;
  safety_action_lock_enabled?: boolean;
  reactions_enabled?: boolean;
  reaction_min_interval_minutes?: number;
  reaction_max_interval_minutes?: number;
  reaction_daily_limit_per_account?: number;
  reaction_daily_limit_per_chat?: number;
  reaction_author_cooldown_hours?: number;
  reaction_skip_chance_percent?: number;
  reaction_emojis?: string[];
}

export interface MailingStats {
  iteration_count: number;
  sent_total: number;
  failed_total: number;
  started_at: string | null;
  current_chat_id: number | null;
}

export interface MailingStatus {
  running: boolean;
  stats: MailingStats;
  sent_24h: number;
  failed_24h: number;
  enabled_chats: number;
  enabled_accounts: number;
}

export interface SendLogItem {
  id: number;
  chat_id: number | null;
  chat_title: string | null;
  account_id: number | null;
  template_id: number | null;
  status: "sent" | "failed" | "skipped" | "deleted";
  error_code: string | null;
  error_text: string | null;
  created_at: string;
}

export interface SendLogResponse {
  items: SendLogItem[];
  has_more: boolean;
}

// ============================================================
// Queries
// ============================================================

export function useMailingSettings() {
  return useQuery<MailingSettings, ApiError>({
    queryKey: ["mailing", "settings"],
    queryFn: () => api.get<MailingSettings>("/mailing/settings"),
    staleTime: 10_000,
  });
}

export function useMailingStatus(opts?: { refetchInterval?: number }) {
  return useQuery<MailingStatus, ApiError>({
    queryKey: ["mailing", "status"],
    queryFn: () => api.get<MailingStatus>("/mailing/status"),
    refetchInterval: opts?.refetchInterval ?? 5_000,
    staleTime: 0,
  });
}

export function useSendLog(params?: { limit?: number; statusFilter?: string }) {
  return useQuery<SendLogResponse, ApiError>({
    queryKey: ["mailing", "log", params?.limit, params?.statusFilter],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (params?.limit) sp.set("limit", String(params.limit));
      if (params?.statusFilter) sp.set("status_filter", params.statusFilter);
      const qs = sp.toString();
      return api.get<SendLogResponse>(`/mailing/log${qs ? `?${qs}` : ""}`);
    },
    staleTime: 5_000,
  });
}

// ============================================================
// Mutations
// ============================================================

export function useUpdateMailingSettings() {
  const qc = useQueryClient();
  return useMutation<MailingSettings, ApiError, MailingSettingsPatch>({
    mutationFn: (body) => api.patch<MailingSettings>("/mailing/settings", body),
    onSuccess: (data) => {
      qc.setQueryData(["mailing", "settings"], data);
      qc.invalidateQueries({ queryKey: ["mailing", "status"] });
    },
  });
}

export function useStartMailing() {
  const qc = useQueryClient();
  return useMutation<MailingStatus, ApiError, void>({
    mutationFn: () => api.post<MailingStatus>("/mailing/start"),
    onSuccess: (data) => {
      qc.setQueryData(["mailing", "status"], data);
      qc.invalidateQueries({ queryKey: ["mailing", "settings"] });
    },
  });
}

export function useStopMailing() {
  const qc = useQueryClient();
  return useMutation<MailingStatus, ApiError, void>({
    mutationFn: () => api.post<MailingStatus>("/mailing/stop"),
    onSuccess: (data) => {
      qc.setQueryData(["mailing", "status"], data);
      qc.invalidateQueries({ queryKey: ["mailing", "settings"] });
    },
  });
}

// ============================================================
// SSE — live monitor
// ============================================================

export type LiveEventKind =
  | "hello"
  | "engine_started"
  | "engine_stopped"
  | "iteration_start"
  | "iteration_done"
  | "iteration_empty"
  | "iteration_error"
  | "send"
  | "skip"
  | "fail"
  | "flood"
  | "quiet_mode"
  | "reaction_worker_started"
  | "reaction_worker_stopped"
  | "reaction_iteration_done"
  | "reaction_iteration_empty"
  | "reaction_iteration_error"
  | "reaction_sent"
  | "reaction_skip"
  | "reaction_fail"
  | "reaction_flood"
  | "reaction_quiet_mode"
  | "join_ok"
  | "join_fail"
  | "join_manual"
  | "heartbeat"
  | "lag";

export interface LiveEvent {
  kind: LiveEventKind;
  // Server sends a stable subset of fields per kind
  data: {
    chat_id?: number;
    chat_title?: string;
    account_id?: number;
    template_id?: number;
    error?: string;
    flood_seconds?: number;
    reason?: string;
    seconds?: number;
    chats_count?: number;
    iteration_no?: number;
    next_in_seconds?: number;
    owner_id?: number;
    message_id?: number;
    target_user_id?: number;
    reaction?: string;
    text_only?: boolean;
    profile_id?: number;
  };
  ts: number; // client-side timestamp (ms) for deduping/keying
}

/**
 * Subscribe to the live SSE stream. Manages connection lifecycle:
 *   - reconnects with exponential backoff on dropped connections
 *   - filters out heartbeats by default (you can opt in)
 *   - returns the last N events as a ring buffer (default 200)
 */
export function useMailingStream(options?: {
  bufferSize?: number;
  includeHeartbeats?: boolean;
  enabled?: boolean;
}) {
  const bufferSize = options?.bufferSize ?? 200;
  const includeHeartbeats = options?.includeHeartbeats ?? false;
  const enabled = options?.enabled ?? true;

  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const esRef = useRef<EventSource | null>(null);
  const retryRef = useRef(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    cancelledRef.current = false;

    function connect() {
      const initData = tg?.initData ?? "";
      // EventSource has no custom-header support, so push initData via query
      const url = `/api/mailing/stream?init_data=${encodeURIComponent(initData)}`;
      const es = new EventSource(url);
      esRef.current = es;

      es.onopen = () => {
        retryRef.current = 0;
        setConnected(true);
      };
      es.onerror = () => {
        setConnected(false);
        es.close();
        if (cancelledRef.current) return;
        const delay = Math.min(15000, 1000 * Math.pow(2, retryRef.current++));
        setTimeout(() => {
          if (!cancelledRef.current) connect();
        }, delay);
      };

      // The server emits per-kind events (event: send, event: fail, …)
      // but we listen on `message` too, in case of generic frames.
      const handler = (e: MessageEvent) => {
        try {
          const parsed = JSON.parse(e.data) as { kind: LiveEventKind; data: LiveEvent["data"] };
          if (!includeHeartbeats && parsed.kind === "heartbeat") return;
          const evt: LiveEvent = { kind: parsed.kind, data: parsed.data ?? {}, ts: Date.now() };
          setEvents((prev) => {
            const next = [evt, ...prev];
            return next.length > bufferSize ? next.slice(0, bufferSize) : next;
          });
        } catch {
          // ignore malformed frame
        }
      };
      // Register per known kind
      const kinds: LiveEventKind[] = [
        "hello",
        "engine_started",
        "engine_stopped",
        "iteration_start",
        "iteration_done",
        "iteration_empty",
        "iteration_error",
        "send",
        "skip",
        "fail",
        "flood",
        "quiet_mode",
        "reaction_worker_started",
        "reaction_worker_stopped",
        "reaction_iteration_done",
        "reaction_iteration_empty",
        "reaction_iteration_error",
        "reaction_sent",
        "reaction_skip",
        "reaction_fail",
        "reaction_flood",
        "reaction_quiet_mode",
        "heartbeat",
        "lag",
      ];
      kinds.forEach((k) => es.addEventListener(k, handler as EventListener));
      es.addEventListener("message", handler as EventListener);
    }

    connect();

    return () => {
      cancelledRef.current = true;
      esRef.current?.close();
      esRef.current = null;
      setConnected(false);
    };
  }, [bufferSize, includeHeartbeats, enabled]);

  return { events, connected };
}
